import React, { useState, useMemo } from 'react';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../libs/axios';
import { Pagination, PaginationType } from '../../utils/pagination.utils';
import { ExcelDownload } from '../../utils/excel.utils';

interface UserProjectEntry {
  project_id: number;
  project_name: string | null;
}

interface UserReportRow {
  user_id: number;
  employee_code: string | null;
  username: string;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
  designation: string | null;
  department: string | null;
  reporting_person: string | null;
  reporting_person_employee_code: string | null;
  reporting_latitude: number | null;
  reporting_longitude: number | null;
  registration_date: string | null;
  registered_by: string | null;
  registered_by_employee_code: string | null;
  last_updated_date: string | null;
  projects: UserProjectEntry[];
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const UsersReport: React.FC = () => {
  const [users, setUsers] = useState<UserReportRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({
    total_items: 0,
    current_page: 1,
    total_pages: 1,
    items_per_page: 50,
    has_next_page: false,
    has_previous_page: false,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [page, setPage] = useState(1);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<Record<string, string | number>[]>([]);

  const fetchUsers = async (targetPage: number = page, targetLimit: number = pagination.items_per_page) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/reports/users', {
        params: { page: targetPage, limit: targetLimit },
      });

      if (response.data.success) {
        const payload = response.data.data || {};
        const rows: UserReportRow[] = payload.data || [];
        setUsers(rows);
        setHasFetched(true);

        const p = payload.pagination || {};
        const total = Number(p.total ?? p.total_items ?? p.total_count) || rows.length;
        const currentPage = Number(p.current_page) || targetPage;
        const perPage = Number(p.per_page ?? p.items_per_page) || targetLimit;
        const pages = Number(p.total_pages) || Math.max(1, Math.ceil(total / perPage));

        setPagination({
          total_items: total,
          current_page: currentPage,
          total_pages: pages,
          items_per_page: perPage,
          has_next_page: Boolean(p.has_next_page ?? currentPage < pages),
          has_previous_page: Boolean(p.has_prev_page ?? currentPage > 1),
        });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch users report');
    } finally {
      setLoading(false);
    }
  };

  const buildExportRows = (rows: UserReportRow[]): Record<string, string | number>[] => {
    const result: Record<string, string | number>[] = [];

    for (const row of rows) {
      const projectNames = row.projects.map((p) => p.project_name || '').filter(Boolean).join(', ') || 'NA';
      const reportingPersonDisplay = row.reporting_person
        ? (row.reporting_person_employee_code ? `${row.reporting_person} (${row.reporting_person_employee_code})` : row.reporting_person)
        : 'NA';
      const registeredByDisplay = row.registered_by
        ? (row.registered_by_employee_code ? `${row.registered_by} (${row.registered_by_employee_code})` : row.registered_by)
        : 'NA';
      result.push({
        'User ID': row.user_id,
        'Employee Code': row.employee_code || 'NA',
        'Username': row.username,
        'First Name': row.first_name || 'NA',
        'Last Name': row.last_name || 'NA',
        'Role': row.role || 'NA',
        'Designation': row.designation || 'NA',
        'Department Group': row.department || 'NA',
        'Reporting Person': reportingPersonDisplay,
        'Reporting Latitude': row.reporting_latitude !== null && row.reporting_latitude !== undefined ? row.reporting_latitude : 'NA',
        'Reporting Longitude': row.reporting_longitude !== null && row.reporting_longitude !== undefined ? row.reporting_longitude : 'NA',
        'Registration Date': row.registration_date || 'NA',
        'Registered By': registeredByDisplay,
        'Last Updated Date': row.last_updated_date || 'NA',
        'Projects Assigned': projectNames,
      });
    }

    return result;
  };

  const fetchExportData = async () => {
    try {
      setExporting(true);
      let currentPage = 1;
      let totalPages = 1;
      const pageSize = 100;
      const allRows: UserReportRow[] = [];

      do {
        const response = await axiosInstance.get('/reports/users', {
          params: { page: currentPage, limit: pageSize },
        });
        if (!response.data.success) break;
        const payload = response.data.data || {};
        const rows: UserReportRow[] = payload.data || [];
        allRows.push(...rows);
        const p = payload.pagination || {};
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total ?? p.total_items ?? p.total_count) || allRows.length) / pageSize));
        currentPage += 1;
      } while (currentPage <= totalPages);

      setExportData(buildExportRows(allRows));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare users report export');
    } finally {
      setExporting(false);
    }
  };

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null,
  };

  const val = (v: string | null | undefined) => v || 'NA';

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Users Report</h1>
            <p className="text-sm text-gray-600 mt-1">Users sharing projects with you and their assignments.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!hasFetched && (
              <button
                onClick={() => fetchUsers(1, pagination.items_per_page)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : null}
                {loading ? 'Loading...' : 'Load Report'}
              </button>
            )}
            <button
              onClick={fetchExportData}
              disabled={!hasFetched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['User ID', 'Employee Code', 'Username', 'First Name', 'Last Name', 'Role', 'Designation', 'Department Group', 'Reporting Person', 'Reporting Lat', 'Reporting Long', 'Registration Date', 'Registered By', 'Last Updated', 'Projects'].map((col) => (
                  <th key={col} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td>
                </tr>
              ) : !hasFetched ? (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-sm text-gray-500">Click "Load Report" to fetch data.</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-4 py-10 text-center text-sm text-gray-600">No users found.</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">{user.user_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{val(user.employee_code)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.username}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{val(user.first_name)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{val(user.last_name)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{val(user.role)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{val(user.designation)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{val(user.department)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {user.reporting_person ? (
                        <span className="text-sm text-gray-800">
                          {user.reporting_person}
                          {user.reporting_person_employee_code && (
                            <span className="ml-1 text-xs text-gray-500">({user.reporting_person_employee_code})</span>
                          )}
                        </span>
                      ) : 'NA'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.reporting_latitude !== null && user.reporting_latitude !== undefined ? user.reporting_latitude : 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.reporting_longitude !== null && user.reporting_longitude !== undefined ? user.reporting_longitude : 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.registration_date || 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {user.registered_by ? (
                        <span>
                          {user.registered_by}
                          {user.registered_by_employee_code && (
                            <span className="ml-1 text-xs text-gray-500">({user.registered_by_employee_code})</span>
                          )}
                        </span>
                      ) : 'NA'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{user.last_updated_date || 'NA'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                      {user.projects.length === 0 ? 'NA' : (
                        <div className="flex flex-wrap gap-1">
                          {user.projects.map((p) => (
                            <span
                              key={p.project_id}
                              className="inline-block px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-100"
                            >
                              {p.project_name || `#${p.project_id}`}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hasFetched && (
          <Pagination
            pagination={paginationAdapter}
            onPageChange={(nextPage) => {
              setPage(nextPage);
              fetchUsers(nextPage, pagination.items_per_page);
            }}
            onLimitChange={(nextLimit) => {
              setPagination((prev) => ({ ...prev, items_per_page: nextLimit }));
              setPage(1);
              fetchUsers(1, nextLimit);
            }}
          />
        )}
      </div>

      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="users_report"
        title="Export Users Report"
        description="Download users report as Excel or CSV"
      />
    </div>
  );
};

export default UsersReport;
